import type { Catalogue } from '@daghfal/core';

/**
 * The nursery admin console's own messages.
 *
 * The register here is the nursery's, not the platform's: "الحضانة" rather than "المشترك", and the
 * owner's own vocabulary for fees and classes.
 */
export const strings: Catalogue = {
  ar: {
    'console.name': 'إدارة الحضانة',
    'console.tagline': 'دغفل',

    'nav.dashboard': 'اللوحة',
    'nav.children': 'الأطفال',
    'nav.classes': 'الصفوف',
    'nav.years': 'السنوات الدراسية',
    'nav.staff': 'الكادر',
    'nav.attendance': 'الحضور',
    'nav.daily': 'الرعاية اليومية',
    'catalog.title': 'خيارات الرعاية',
    'catalog.hint': 'ما تختار منه التسجيلات السريعة والوجبات وأشكال الأدوية. الخيار المُوقَف يختفي من القوائم ويبقى على التسجيلات التي استعملته.',
    'catalog.type': 'القائمة',
    'catalog.type.MealType': 'الوجبات',
    'catalog.type.Food': 'الأصناف',
    'catalog.type.IntakeLevel': 'المشروبات',
    'catalog.type.Mood': 'الحالات المزاجية',
    'catalog.type.Activity': 'الأنشطة',
    'catalog.type.DiaperStatus': 'تغيير الحفاض',
    'catalog.type.Occasion': 'المناسبات',
    'catalog.type.MedicationForm': 'أشكال الأدوية',
    'catalog.empty': 'لا شيء في هذه القائمة بعد.',
    'catalog.retired': 'مُوقَف',
    'catalog.retire': 'إيقاف',
    'catalog.restore': 'إعادة',
    'catalog.editOf': 'تعديل {name}',
    'catalog.retireOf': 'إيقاف {name}',
    'catalog.restoreOf': 'إعادة {name}',
    'catalog.nameAr': 'الاسم بالعربية',
    'catalog.nameEn': 'الاسم بالإنكليزية',
    'catalog.icon': 'الرمز',
    'catalog.order': 'الترتيب',
    'catalog.unitAr': 'الوحدة بالعربية',
    'catalog.unitEn': 'الوحدة بالإنكليزية',
    'catalog.add': 'إضافة',
    'daily.title': 'الرعاية اليومية',
    'daily.lede': 'يوم الصف، ويوم كل طفل فيه، للاطّلاع والتصحيح والنشر.',
    'nav.photos': 'الصور',
    'photos.lede': 'صور الأطفال حسب الصف واليوم والمناسبة: صحّح من في الصورة وتعليقها، أو احذفها، أو أعد نشر ما سُحب بعد تسجيل موافقة جديدة.',
    'photos.allClasses': 'كل الصفوف',
    'photos.statusFilter': 'حالة الصورة',
    'photos.from': 'من تاريخ',
    'photos.to': 'إلى تاريخ',
    'occasions.new': 'مناسبة جديدة',
    'occasions.hint': 'تُجمع تحتها الصور عند الرفع، ويصفّي بها الأهالي معرضهم.',
    'occasions.name': 'اسم المناسبة',
    'occasions.date': 'تاريخها',
    'occasions.type': 'نوعها',
    'occasions.create': 'إضافة المناسبة',
    'occasions.created': 'أُضيفت المناسبة.',
    'daily.recorded': 'ما سُجّل وما ينقص',
    'daily.asleep': '😴 نائم/ة منذ {time}',
    'daily.open': 'فتح اليوم',
    'daily.openOf': 'فتح يوم {name}',
    'class.empty': 'لا يوجد أطفال في هذا الصف.',
    'nav.finance': 'المالية',
    'nav.complaints': 'الشكاوى',
    'nav.reports': 'التقارير',
    'nav.settings': 'الإعدادات',
    'nav.notifications': 'الإشعارات',

    'dashboard.title': 'لوحة الحضانة',
    'dashboard.registered': 'الأطفال المسجّلون',
    'dashboard.presentToday': 'حاضرون اليوم',
    'dashboard.absentToday': 'غائبون اليوم',
    'dashboard.incomeThisMonth': 'دخل هذا الشهر',
    'dashboard.collected': 'المُحصَّل',
    'dashboard.outstanding': 'المتبقّي',
    'dashboard.classes': 'الصفوف الفعّالة',
    'dashboard.criticalAllergies': 'أطفال لديهم حساسية حرجة',

    'children.title': 'الأطفال',
    'children.lede': 'سجلّ الأطفال المسجّلين في الحضانة.',
    'children.search': 'ابحث بالاسم',
    'childPicker.placeholder': 'اكتب الاسم أو رقم وليّ الأمر',
    'childPicker.matches': 'الأطفال المطابقون',
    'childPicker.none': 'لا يوجد طفل مسجّل يطابق ما كتبته.',
    'childPicker.more': 'نتائج أخرى لم تظهر: {count}. اكتب المزيد من الاسم لتضييق البحث.',
    'childPicker.unchosen': 'اختر الطفل من القائمة التي تظهر تحت الحقل.',
    'children.new': 'تسجيل طفل',
    'children.caption': 'جدول الأطفال المسجّلين',
    'children.empty': 'لا يوجد أطفال مطابقون.',
    'children.column.name': 'الاسم',
    'children.column.class': 'الصف',
    'children.column.dob': 'تاريخ الميلاد',
    'children.column.age': 'العمر',
    'children.column.gender': 'الجنس',
    'children.column.status': 'الحالة',
    'children.allClasses': 'كل الصفوف',
    'children.unassigned': 'بلا صف',

    'child.title': 'ملف الطفل',
    'child.enrolled': 'تاريخ التسجيل',
    'child.enrolled.hint': 'يبدأ التسجيل الحالي من هذا التاريخ. لا يأتي بعد أول يوم سُجّل فيه حضور الطفل أو رعايته.',
    'child.diaperLog': 'تسجيل الحفاضات',
    'child.diaperLog.hint': 'لطفل ترك الحفاضات في صف الصغار، أو طفل أكبر ما زال يحتاجها.',
    'child.diaperLog.Class': 'حسب إعداد الصف',
    'child.diaperLog.On': 'نعم، لهذا الطفل',
    'child.diaperLog.Off': 'لا، لهذا الطفل',
    'child.diaperLog.yes': 'تُسجَّل له الحفاضات',
    'child.diaperLog.no': 'لا تُسجَّل له الحفاضات',
    'guardians.empty': 'لم يُربط أي وليّ أمر بهذا الطفل بعد.',
    'guardians.name': 'اسم وليّ الأمر',
    'guardians.name.required': 'أدخل اسم وليّ الأمر.',
    'guardians.edit': 'تعديل الربط',
    'guardians.suspended': 'موقوف',
    'guardians.historyFrom': 'يرى سجلات الطفل من',
    'guardians.historyFrom.hint': 'ما يُفتح لوليّ الأمر لا يمكن سحبه بعد أن يراه. ابدأ من اليوم، ووسّع لاحقًا إن لزم.',
    'guardians.historyFrom.today': 'من اليوم',
    'guardians.historyFrom.enrolment': 'من تاريخ التحاق الطفل',
    'guardians.historyFrom.date': 'من تاريخ آخر',
    'guardians.historyFrom.shown': 'يرى السجلات من {date}',
    'guardians.suspend': 'إيقاف وليّ الأمر',
    'guardians.suspend.hint': 'وليّ الأمر الموقوف يبقى مسجّلًا، لكنه لا يرى شيئًا عن الطفل، ولا تصله إشعارات، ولا يُسلَّم إليه الطفل.',
    'guardians.suspend.note': 'سبب الإيقاف',
    'guardians.suspend.evidence': 'رقم القرار أو المستند',
    'guardians.suspend.noteRequired': 'اكتب سبب الإيقاف.',
    'guardians.unlink': 'إنهاء الربط',
    'guardians.unlink.hint': 'ينتهي ارتباط وليّ الأمر بالطفل اليوم، فلا يرى عنه شيئًا بعدها. يبقى في السجل أنه كان وليّ أمره.',
    'guardians.unlink.confirm': 'تأكيد إنهاء الربط',
    'guardians.ended.show': 'أولياء الأمور السابقون',
    'guardians.ended.hide': 'إخفاء أولياء الأمور السابقين',
    'guardians.ended.empty': 'لا يوجد أولياء أمور سابقون.',
    'guardians.ended.Unlinked': 'انتهى ربطه في {date}',
    'guardians.ended.ChildWithdrawn': 'انتهى ربطه بمغادرة الطفل في {date}',
    'legalHold.title': 'تجميد الحذف',
    'legalHold.hint': 'ما دام التجميد قائمًا لا يُحذف شيء من سجلات الطفل ولا من الصور التي يظهر فيها عند انتهاء مدة الاحتفاظ. يضعه المالك وحده، ويُسجَّل في سجل التدقيق.',
    'legalHold.none': 'لا يوجد تجميد. تُحذف سجلات الطفل عند انتهاء مدد الاحتفاظ بعد مغادرته.',
    'legalHold.held': 'مجمّد منذ {date} بقرار {name}',
    'legalHold.held.unknown': 'مجمّد منذ {date}',
    'legalHold.reason': 'السبب',
    'legalHold.reason.hint': 'الخلاف أو الشكوى أو القضية التي يُجمَّد الحذف من أجلها.',
    'legalHold.reason.required': 'اكتب سبب التجميد.',
    'legalHold.place': 'تجميد الحذف',
    'legalHold.lift': 'رفع التجميد',
    'legalHold.lift.confirm': 'بعد رفع التجميد يُحذف من سجلات الطفل ما انتهت مدته في المرة القادمة. هل تريد رفعه؟',
    'restrictions.title': 'لا يُسلَّم الطفل إلى',
    'restrictions.hint': 'يظهر كل اسم هنا بالأحمر لموظفة الاستقبال عند تسجيل المغادرة، مع ملاحظته.',
    'restrictions.add': 'إضافة ممنوع من الاستلام',
    'restrictions.empty': 'لا يوجد ممنوعون من استلام الطفل.',
    'restrictions.name': 'الاسم',
    'restrictions.name.required': 'أدخل اسم الشخص.',
    'restrictions.note': 'ما يجب أن تعرفه الموظفة عند الباب',
    'restrictions.note.hint': 'السبب، وما تفعله إن حضر هذا الشخص.',
    'restrictions.note.required': 'اكتب ملاحظة للموظفة عند الباب.',
    'restrictions.evidence': 'رقم القرار أو المستند',
    'restrictions.evidence.shown': 'المستند: {reference}',
    'restrictions.lift': 'رفع المنع',
    'restrictions.lifted': 'رُفع المنع في {date}',
    'restrictions.lifted.show': 'إظهار الممنوعين سابقًا',
    'restrictions.lifted.hide': 'إخفاء الممنوعين سابقًا',
    'child.allergies': 'الحساسيات',
    'child.allergies.noneKnown': 'لا توجد حساسية معروفة، بحسب إجابة العائلة.',
    'child.allergies.unanswered': 'لم تُسجَّل إجابة عن الحساسية بعد. أضف حساسيات الطفل، أو اختر «لا توجد حساسية معروفة» إن أكّدت العائلة ذلك.',
    'child.edit': 'تعديل',
    'child.emergencyContacts': 'جهات الاتصال في الطوارئ',
    'child.emergencyContacts.none': 'لم تُسجَّل جهات اتصال للطوارئ. أضف جهة واحدة على الأقل.',
    'child.severity.Mild': 'خفيفة',
    'child.severity.Severe': 'شديدة',
    'child.severity.Anaphylactic': 'مفرطة (تهدّد الحياة)',
    'child.withdraw': 'إنهاء التسجيل',
    'child.withdraw.reason': 'سبب إنهاء التسجيل',
    'child.withdraw.hint':
      'يتوقّف إصدار الفواتير المستقبلية ويبقى سجلّ الطفل وتاريخه كما هو، لا يُحذف شيء. وينتهي ارتباط أولياء الأمور بالطفل فلا يرون عنه شيئًا، إلا صفحة المدفوعات ما دام عليه رصيد مستحق.',
    'child.withdraw.copies': 'بعد إنهاء التسجيل تستطيع إعداد نسخة من بيانات الطفل لكل وليّ أمر يطلبها، من بطاقة «نسخ بيانات الطفل».',
    'exports.title': 'نسخ بيانات الطفل',
    'exports.hint':
      'ملف PDF محمي بكلمة مرور لوليّ أمر واحد، فيه ما يطّلع عليه في التطبيق، دون صور أو فيديو. تصله كلمة المرور برسالة نصية ولا تراها الإدارة. نزّل الملف وسلّمه له.',
    'exports.afterWithdrawal': 'انتهى تسجيل الطفل. أعِدّ من هنا نسخة لكل وليّ أمر يطلبها، خلال المهلة المحددة في الإعدادات.',
    'exports.recipients': 'لمن تُعدّ النسخة',
    'exports.make': 'إعداد نسخة',
    'exports.made': 'أُعدّت النسخة، وأُرسلت كلمة مرورها إلى {name} برسالة نصية. نزّل الملف من القائمة أدناه وسلّمه له.',
    'exports.ended': 'انتهى تسجيل الطفل في {date}',
    'exports.list': 'النسخ المُعدّة',
    'exports.none': 'لم تُعدّ أي نسخة بعد.',
    'exports.covers': 'تغطّي الفترة {from} – {to}',
    'exports.madeBy': 'أعدّها {name} في {date}',
    'exports.downloads': 'عدد مرات التنزيل: {count}',
    'exports.keptUntil': 'يُحذف الملف في {date}',
    'exports.download': 'تنزيل الملف',
    'exports.downloading': 'جارٍ التنزيل…',
    'exports.fileGone': 'حُذف الملف',
    'handover.title': 'تسليم الصور',
    'handover.hint': 'لا تحوي النسخة صورًا. إن سلّمتم صور الطفل لوليّ أمره خارج التطبيق، سجّلوا ذلك هنا.',
    'handover.guardian': 'وليّ الأمر الذي استلمها',
    'handover.guardianRequired': 'اختر وليّ الأمر الذي استلم الصور.',
    'handover.date': 'تاريخ التسليم (اليوم إن تُرك فارغًا)',
    'handover.note': 'ما سُلّم وكيف',
    'handover.record': 'تسجيل التسليم',
    'handover.none': 'لم يُسجَّل أي تسليم.',
    'handover.entry': '{date}: إلى {name}، وسجّله {by}',
    'child.reenroll': 'إعادة التسجيل',
    'child.reenroll.hint': 'يبدأ تسجيل جديد، ويبقى التسجيل السابق وكل ما سُجّل فيه كما هو.',
    'child.reenroll.date': 'تاريخ العودة',
    'child.reenroll.restore': 'أولياء الأمور الذين يعودون مع الطفل',
    'child.reenroll.guardian': '{name} ({relationship})',
    'child.reenroll.noFormer': 'لا يوجد أولياء أمور انتهى ارتباطهم بمغادرة الطفل. أضِفهم من بطاقة أولياء الأمور بعد إعادة التسجيل.',
    'child.reenroll.confirm': 'تأكيد إعادة التسجيل',
    'child.withdraw.confirm': 'تأكيد إنهاء التسجيل',
    'child.details': 'البيانات الأساسية',
    'import.title': 'استيراد من جدول',
    'import.lede': 'تسجيل أطفال الحضانة دفعة واحدة من جدول محفوظ بصيغة CSV. يُفحص الملف أولاً، ولا يُستورد إلا إذا سلمت كل صفوفه.',
    'import.file': 'ملف CSV',
    'import.file.hint': 'احفظ الجدول بصيغة CSV (UTF-8). الصف الأول عناوين الأعمدة.',
    'import.columns': 'عناوين الأعمدة، بالعربية أو كما يلي:',
    'import.unreadable': 'تعذّرت قراءة الملف. احفظه من جدول البيانات بصيغة CSV (UTF-8) ثم اختره من جديد.',
    'import.empty': 'لا توجد في الملف صفوف بعد صف العناوين.',
    'import.unknownColumns': 'لم يُقرأ ما في هذه الأعمدة لأن عناوينها غير معروفة: {columns}.',
    'import.checking': 'جارٍ فحص الصفوف…',
    'import.checked': 'نتيجة الفحص',
    'import.done': 'تم الاستيراد',
    'import.valid': 'صالحة: {count}',
    'import.invalid': 'فيها أخطاء: {count}',
    'import.fixFirst': 'صحّح الصفوف التي فيها أخطاء في الجدول ثم اختر الملف من جديد. لا يُستورد شيء حتى تسلم كلها.',
    'import.commit.one': 'استيراد طفل واحد',
    'import.commit.two': 'استيراد طفلين',
    'import.commit.few': 'استيراد {count} أطفال',
    'import.commit.many': 'استيراد {count} طفلاً',
    'import.commit.other': 'استيراد {count} طفل',
    'import.committed.one': 'سُجّل طفل واحد.',
    'import.committed.two': 'سُجّل طفلان.',
    'import.committed.few': 'سُجّل {count} أطفال.',
    'import.committed.many': 'سُجّل {count} طفلاً.',
    'import.committed.other': 'سُجّل {count} طفل.',
    'import.rows': 'صفوف الملف',
    'import.row': 'الصف',
    'import.verdict': 'النتيجة',
    'child.transfer': 'النقل إلى صف آخر',
    'child.transfer.hint': 'تبقى الأيام السابقة للنقل مسجّلة في الصف الذي كانت فيه.',
    'child.transfer.to': 'الصف الجديد',
    'child.transfer.date': 'من تاريخ',
    'child.transfer.date.hint': 'اليوم إن تُرك فارغاً.',
    'child.transfer.reason': 'السبب',
    'child.transfer.submit': 'نقل',
    'child.transfer.done': 'نُقل الطفل.',
    'child.transfer.overCapacity': 'نُقل الطفل، وتجاوز الصف سعته الآن: {occupancy} من {capacity}.',

    'children.new.title': 'تسجيل طفل جديد',
    'children.new.firstName': 'الاسم الأول',
    'children.new.lastName': 'اسم العائلة',
    'children.new.preferredName': 'الاسم المُنادى به',
    'children.new.preferredName.hint': 'الاسم الذي يُنادى به الطفل يومياً، إن اختلف عن اسمه الأول.',
    'children.new.dob': 'تاريخ الميلاد',
    'children.new.gender': 'الجنس',
    'children.new.class': 'الصف',
    'children.new.enrollmentDate': 'تاريخ التسجيل',
    'children.new.allergies': 'الحساسيات',
    'children.new.allergen': 'المادة المُسبِّبة',
    'children.new.severity': 'الشدّة',
    'children.new.reaction': 'الأعراض',
    'children.new.actionRequired': 'الإجراء المطلوب',
    'children.new.addAllergy': 'إضافة حساسية',
    'children.new.noKnownAllergies': 'لا توجد حساسية معروفة (أكّدت العائلة ذلك)',
    'children.new.allergies.hint': 'أضف حساسيات الطفل، أو اختر «لا توجد حساسية معروفة». الإجابة مطلوبة.',
    'children.new.emergencyContacts.hint': 'أضف جهة اتصال واحدة على الأقل للطوارئ. مطلوبة.',
    'registrationGaps.title': 'تسجيل غير مكتمل',
    'registrationGaps.lede': 'أطفال سُجّلوا قبل أن تصبح جهة اتصال الطوارئ والإجابة عن الحساسية مطلوبتين. افتح صفحة كل طفل لإكمال ما ينقصه.',
    'registrationGaps.column.missing': 'الناقص',
    'registrationGaps.contact': 'جهة اتصال للطوارئ',
    'registrationGaps.allergies': 'الإجابة عن الحساسية',
    'registrationGaps.empty': 'لا ينقص تسجيلَ أي طفل شيء.',
    'registrationGaps.notice': 'عدد الأطفال الذين ينقص تسجيلهم جهة اتصال للطوارئ أو إجابة عن الحساسية: {count}.',
    'registrationGaps.open': 'عرض القائمة',
    'children.new.emergencyContacts': 'جهات الاتصال في الطوارئ',
    'children.new.contactName': 'الاسم',
    'children.new.contactRelationship': 'صلة القرابة',
    'children.new.contactPhone': 'الهاتف',
    'children.new.addContact': 'إضافة جهة اتصال',
    'children.new.remove': 'حذف',
    'children.new.submit': 'تسجيل الطفل',
    'children.new.photo.hint': 'اختيارية. تُرفع بعد تسجيل الطفل، ويمكن إضافتها أو تغييرها لاحقاً من صفحته.',
    'children.new.photoFailed': 'سُجّل الطفل، لكن تعذّر رفع الصورة. أضفها من بطاقة «صورة الطفل» أدناه.',

    'classes.title': 'الصفوف',
    'classes.lede': 'الصفوف وسعتها والفئة العمرية لكل منها.',
    'classes.new': 'إضافة صف',
    'classes.caption': 'جدول الصفوف',
    'classes.empty': 'لم تُنشأ صفوف بعد.',
    'classes.column.name': 'الصف',
    'classes.column.ages': 'الفئة العمرية',
    'classes.column.capacity': 'السعة',
    'classes.column.enrolled': 'المسجّلون',
    'classes.column.room': 'الغرفة',
    'classes.column.year': 'السنة الدراسية',
    'classes.ageRange': '{min}–{max} شهراً',
    'classes.occupancy': '{enrolled} من {capacity}',
    'classes.full': 'مكتمل',
    'classes.archived': 'مؤرشف',
    'classes.archive': 'أرشفة',
    'classes.archive.confirm': 'تأكيد الأرشفة',
    'classes.archiveOf': 'أرشفة صف {name}',
    'classes.editOf': 'تعديل صف {name}',
    'classes.new.name': 'اسم الصف',
    'classes.new.ageMin': 'أصغر عمر (بالأشهر)',
    'classes.new.ageMax': 'أكبر عمر (بالأشهر)',
    'classes.new.capacity': 'السعة',
    'classes.new.room': 'الغرفة',
    'classes.new.year': 'السنة الدراسية',
    'classes.new.diaperLog': 'تفعيل تسجيل الحفاضات لهذا الصف',
    'classes.new.diaperLog.hint': 'يظهر زر «حفاض» للمعلّمات في صفوف الأعمار الصغيرة فقط.',
    'classes.new.submit': 'إنشاء الصف',
    'classes.filter.year': 'السنة الدراسية',
    'classes.filter.allYears': 'كل السنوات',
    'classes.new.year.current': '{name} (الحالية)',

    'years.title': 'السنوات الدراسية',
    'years.lede': 'تبدأ رؤية المعلّمات والمساعدات والاستقبال لسجلات الأطفال من أول يوم في السنة الحالية. الإدارة ترى كل السنوات.',
    'years.new': 'إضافة سنة',
    'years.new.submit': 'إضافة السنة',
    'years.edit': 'تعديل السنة',
    'years.empty': 'لا توجد سنوات دراسية بعد.',
    'years.field.name': 'اسم السنة، مثل 2026/2027',
    'years.field.start': 'البداية',
    'years.field.end': 'النهاية',
    'years.status.current': 'الحالية',
    'years.status.planned': 'مخطَّط لها',
    'years.status.closed': 'مغلقة',
    'years.span': 'من {start} إلى {end}',
    'years.classCount.zero': 'لا صفوف فيها',
    'years.classCount.one': 'صف واحد',
    'years.classCount.two': 'صفّان',
    'years.classCount.few': '{count} صفوف',
    'years.classCount.many': '{count} صفاً',
    'years.classCount.other': '{count} صف',

    // ---- The year's fee (Q3, Q44) ----
    'yearFees.title': 'رسوم السنة',
    'finance.tab.payments': 'الدفعات',
    'yearFees.year': 'السنة الدراسية',
    'yearFees.deadline.default': 'موعد سداد رسوم هذه السنة: {date}. يمكن تحديد موعد مختلف لطفل بعينه مع ذكر السبب.',
    'yearFees.deadline.none': 'لا يوجد في هذه السنة الفصل الذي يُحسب منه موعد السداد. أضف فصولها من صفحة السنوات الدراسية، أو أدخل موعداً لكل طفل.',
    'yearFees.filter.all': 'كل الأطفال',
    'yearFees.filter.owing': 'عليهم مبلغ متبقٍّ',
    'yearFees.filter.missing': 'لم يُحدَّد مبلغهم',
    'yearFees.filter.paid': 'سدّدوا كامل المبلغ',
    'yearFees.total': 'المبلغ الكلي',
    'yearFees.paid': 'المدفوع',
    'yearFees.remaining': 'المتبقي',
    'yearFees.deadline': 'الموعد النهائي',
    'yearFees.daysLeft': 'حتى الموعد',
    'yearFees.dueToday': 'اليوم',
    'yearFees.daysLeft.zero': 'اليوم',
    'yearFees.daysLeft.one': 'يوم واحد',
    'yearFees.daysLeft.two': 'يومان',
    'yearFees.daysLeft.few': '{count} أيام',
    'yearFees.daysLeft.many': '{count} يوماً',
    'yearFees.daysLeft.other': '{count} يوم',
    'yearFees.daysPast.zero': 'اليوم',
    'yearFees.daysPast.one': 'متأخر يوماً واحداً',
    'yearFees.daysPast.two': 'متأخر يومين',
    'yearFees.daysPast.few': 'متأخر {count} أيام',
    'yearFees.daysPast.many': 'متأخر {count} يوماً',
    'yearFees.daysPast.other': 'متأخر {count} يوم',
    'yearFees.notSet': 'لم يُحدَّد',
    'yearFees.set': 'تحديد المبلغ',
    'yearFees.change': 'تعديل',
    'yearFees.statement': 'كشف الحساب',
    'yearFees.statementOf': 'كشف حساب {name}',
    'yearFees.empty': 'لا يوجد أطفال في هذه القائمة.',
    'yearFees.form.title': 'رسوم السنة: {name}',
    'yearFees.form.amount': 'المبلغ الكلي للسنة',
    'yearFees.form.deadline': 'الموعد النهائي',
    'yearFees.form.deadlineHint': 'اتركه كما هو ليكون موعد السنة. الموعد المختلف يحتاج سبباً.',
    'yearFees.form.reason': 'السبب',
    'yearFees.form.reasonHint': 'مطلوب عند تعديل مبلغ حُدِّد سابقاً، وعند موعد يختلف عن موعد السنة. يُحفظ مع الفاتورة.',
    'years.current.hint': 'هذه هي السنة الحالية. إن غيّرت تاريخ بدايتها تغيّر معها أول يوم تراه المعلّمات من سجلات الأطفال.',
    'years.makeCurrent': 'اجعلها السنة الحالية',
    'years.delete': 'حذف',
    'years.actions': 'إجراءات',
    'years.terms.caption': 'فصول السنة {year}',
    'years.terms.empty': 'لا فصول في هذه السنة.',
    'years.term.name': 'الفصل الدراسي',
    'years.term.status': 'الحالة',
    'years.term.open': 'مفتوح',
    'years.term.closed': 'مُقفَل',
    'years.term.new': 'إضافة فصل',
    'years.term.new.submit': 'إضافة الفصل',
    'years.term.edit': 'تعديل الفصل',
    'years.term.close': 'إقفال الفصل',
    'years.term.reopen': 'إعادة فتح الفصل',
    'years.term.reopen.title': 'إعادة فتح {name}',
    'years.term.reopen.hint': 'إعادة الفتح تسمح بتعديل سجلات هذا الفصل من جديد: الحضور والتقارير اليومية والحوادث والأدوية والصور. يُحفظ السبب في سجل التدقيق. أقفل الفصل ثانيةً بعد التصحيح.',
    'years.term.reopen.reason': 'سبب إعادة الفتح',
    'years.term.reopenedNote': 'أُعيد فتحه في {date}: {reason}',
    'years.rollover': 'نهاية السنة',

    // The end of the academic year (Q36): a plan reviewed before anything changes.
    'rollover.title': 'نهاية السنة {year}',
    'rollover.title.plain': 'نهاية السنة',
    'rollover.lede': 'راجع صفوف السنة الجديدة ومكان كل طفل فيها قبل إغلاق السنة. الاقتراح مبني على عمر الطفل في أول يوم من السنة الجديدة، وكل ما فيه قابل للتعديل.',
    'rollover.summary': 'تُغلق السنة {year} بعد آخر يوم فيها ({end})، وتصبح {next} السنة الحالية. ينتقل الأطفال إلى صفوفهم الجديدة اعتباراً من {start}. لا تُصدَر رسوم السنة الجديدة تلقائياً، ويبقى أي مبلغ غير مدفوع على حساب الطفل.',
    'rollover.classes': 'صفوف السنة الجديدة',
    'rollover.classes.empty': 'لا صفوف فعّالة في هذه السنة.',
    'rollover.class.carry': 'تنتقل',
    'rollover.class.carryOne': 'نقل صف {name} إلى السنة الجديدة',
    'rollover.class.now': 'الصف هذه السنة',
    'rollover.class.next': 'اسمه في {year}',
    'rollover.class.children': 'الأطفال',
    'rollover.children': 'الأطفال',
    'rollover.children.empty': 'لا أطفال فعّالين في صفوف هذه السنة.',
    'rollover.child.name': 'الطفل',
    'rollover.child.from': 'صفه الآن',
    'rollover.child.age': 'عمره في بداية السنة الجديدة',
    'rollover.child.to': 'السنة الجديدة',
    'rollover.child.toOne': 'مكان {name} في السنة الجديدة',
    'rollover.child.graduate': 'يتخرّج',
    'rollover.months.zero': 'أقل من شهر',
    'rollover.months.one': 'شهر واحد',
    'rollover.months.two': 'شهران',
    'rollover.months.few': '{count} أشهر',
    'rollover.months.many': '{count} شهراً',
    'rollover.months.other': '{count} شهر',
    'rollover.close': 'إغلاق السنة',
    'rollover.close.warning': 'لا يمكن التراجع عن الإغلاق. ستُقفَل فصول السنة المفتوحة ({terms}) فتصير سجلاتها للقراءة فقط، وتنتهي تكليفات الكادر بصفوفها ({assignments}) في آخر يوم من السنة، وتُؤرشف صفوفها، ويُسجَّل المتخرّجون منسحبين بسبب التخرّج فتنتهي روابط أولياء أمورهم.',
    'rollover.close.notYet': 'يمكن إغلاق السنة ابتداءً من {date}. راجع الخطة الآن وعد في ذلك اليوم.',
    'rollover.close.undecided': 'اختر مكان {name} في السنة الجديدة أو تخرّجه: صفه المقترح لم يعد ينتقل.',
    'rollover.close.year': 'السنة التي تصبح الحالية',
    'rollover.close.submit': 'إغلاق السنة {year}',
    'rollover.done.title': 'أُغلقت السنة {year}، وأصبحت {next} السنة الحالية',
    'rollover.done.body': 'صفوف جديدة: {classes}. أطفال انتقلوا: {moved}. متخرّجون: {graduated}. تكليفات انتهت: {assignments}. كلّف الكادر بالصفوف الجديدة من صفحة الكادر، وأدخل رسوم السنة الجديدة من صفحة رسوم السنة.',
    'rollover.done.back': 'العودة إلى السنوات الدراسية',


    // Complaints (§4.14). Written for someone working a queue: what is in front of them, who owns it, and
    // -- above all -- which box sends words to the family and which does not.
    'complaints.title': 'الشكاوى',
    'complaints.lede': 'شكاوى الأهالي ومتابعتها. الشكوى المقدَّمة بحقّك لا تظهر لك هنا.',
    'complaints.tab.unassigned': 'غير مُسندة',
    'complaints.tab.mine': 'مُسندة إليّ',
    'complaints.tab.open': 'المفتوحة',
    'complaints.tab.all': 'الكل',
    'complaints.empty': 'لا توجد شكاوى مطابقة.',
    'complaints.subject': 'الموضوع',
    'complaints.assignedTo': 'المسؤول',
    'complaints.unassigned': 'لم تُسنَد بعد',
    'complaints.raisedOn': 'تاريخ التقديم',
    'complaints.raisedBy': 'مقدّم الشكوى',
    'complaints.overdue': 'تجاوزت المهلة',
    'complaints.about.is': 'هذه الشكوى مقدَّمة بحقّ {name}. لا يطّلع هذا الموظّف عليها ولا يعلم بوجودها.',
    'complaints.handling': 'المعالجة',
    'complaints.acknowledge': 'تأكيد الاستلام',
    'complaints.start': 'بدء المعالجة',
    'complaints.assign': 'إسناد',
    'complaints.reassign': 'نقل الإسناد',
    'complaints.assignTo': 'إسناد المتابعة إلى',
    'complaints.assign.hint': 'تظهر هنا أسماء من يملكون صلاحية معالجة الشكاوى فقط، ولا يظهر الموظّف الذي تخصّه الشكوى.',
    'complaints.assign.choose': 'اختر الشخص',
    'complaints.openCount': '{count} شكوى مفتوحة',
    'complaints.currentlyWith': 'المتابعة حالياً لدى {name}',
    'complaints.timeline': 'سجلّ المعالجة',
    'complaints.internal': 'داخلي',
    'complaints.note': 'ملاحظة داخلية',
    'complaints.note.who': 'لا يراها الأهل',
    'complaints.note.hint': 'للتوثيق الداخلي بين الإدارة. لا تصل إلى وليّ الأمر.',
    'complaints.note.add': 'حفظ الملاحظة',
    'complaints.reply': 'الردّ على وليّ الأمر',
    'complaints.reply.warning': 'ما تكتبه هنا يصل إلى وليّ الأمر ويقرؤه كما هو. للتوثيق الداخلي استخدم «ملاحظة داخلية» أعلاه.',
    'complaints.reply.hint': 'اكتب ردّاً موجّهاً إلى الأهل.',
    'complaints.reply.send': 'إرسال إلى وليّ الأمر',
    'complaints.resolve': 'إنهاء المعالجة',
    'complaints.resolve.hint': 'يصل هذا النصّ إلى وليّ الأمر ويُعرض له كخلاصة لما جرى.',
    'complaints.resolve.summary': 'خلاصة المعالجة',
    'complaints.resolve.summary.hint': 'ما الذي تم بشأن هذه الشكوى؟ اكتبه لوليّ الأمر.',
    'complaints.close': 'إغلاق الشكوى',
    'complaints.reopen': 'إعادة الفتح',

    'staff.title': 'الكادر',
    'attendance.title': 'الحضور',
    'finance.title': 'المالية',
    'reports.title': 'التقارير',
    'reports.month': 'الشهر',
    'reports.year': 'السنة',
    'reports.allClasses': 'كل الصفوف',
    'reports.noClasses': 'لا صفوف بعد. أنشئ صفاً من صفحة الصفوف.',
    'reports.percent': '{value}٪',
    'reports.listSeparator': '، ',
    'reports.attendance.title': 'حضور الصفوف',
    'reports.attendance.summary': 'من {from} إلى {to}: {operating} يوم دوام، {present} يوم حضور، {absent} يوم غياب، {notified} يوم غياب مبلَّغ عنه.',
    'reports.attendance.days': 'أيام الدوام',
    'reports.attendance.present': 'حضر',
    'reports.attendance.absent': 'غاب',
    'reports.attendance.notified': 'غياب مبلَّغ عنه',
    'reports.attendance.rate': 'نسبة الحضور',
    'reports.attendance.empty': 'لم يكن في هذا الصف أطفال في هذا الشهر.',
    'reports.dailyCare.title': 'اكتمال اليوميات',
    'reports.dailyCare.span': 'من {from} إلى {to}.',
    'reports.dailyCare.hint': 'أيام الأطفال: الأيام التي سُجّل فيها حضور طفل في صفّ الموظف وهو معيَّن فيه. المكتملة: ما فيه كل تسجيلات اليوم المعتادة، كما تطلبها لوحة الصف. الصف الذي يعمل فيه اثنان يُحسب لكلٍّ منهما.',
    'reports.dailyCare.staff': 'الموظف',
    'reports.dailyCare.classes': 'الصفوف',
    'reports.dailyCare.childDays': 'أيام الأطفال',
    'reports.dailyCare.complete': 'المكتملة',
    'reports.dailyCare.share': 'النسبة',
    'reports.dailyCare.missing': 'الأكثر نقصاً',
    'reports.dailyCare.missingItem': '{kind} ({count})',
    'reports.dailyCare.empty': 'لم يكن أحد معيَّناً في صف في هذا الشهر.',
    'reports.dailyCare.view.staff': 'حسب كادر الصف',
    'reports.dailyCare.view.recorders': 'حسب من سجّل',
    'reports.dailyCare.recorders.title': 'اليوميات حسب من سجّل',
    'reports.dailyCare.recorders.hint': 'كل موظف سجّل شيئاً في الشهر، وما سجّله من كل نوع. أيام الأطفال: الأيام التي سجّل فيها لطفل شيئاً واحداً على الأقل. المكتملة: ما فيه كل تسجيلات اليوم المعتادة، أيّاً كان من سجّل باقيها.',
    'reports.dailyCare.recorders.entries': 'التسجيلات',
    'reports.dailyCare.recorders.byKind': 'حسب النوع',
    'reports.dailyCare.recorders.empty': 'لم يسجّل أحد شيئاً في هذا الشهر.',
    'reports.roster.title': 'سجلّ الأطفال',
    'reports.roster.preferred': '{name} ({preferred})',
    'reports.roster.gender': 'الجنس',
    'reports.roster.dateOfBirth': 'تاريخ الميلاد',
    'reports.roster.enrolled': 'تاريخ التسجيل',
    'reports.roster.guardians': 'أولياء الأمور',
    'reports.roster.guardian': '{name} ({relationship})',
    'reports.roster.empty': 'لا أطفال مسجَّلون.',
    'finance.printIncome': 'طباعة تقرير الدخل',
    'yearFees.printBalances': 'طباعة الأرصدة المستحقة',
    'settings.title': 'إعدادات الحضانة',
    'identity.title': 'بيانات الحضانة',
    'identity.hint': 'رابط الدخول والمنطقة الزمنية والعملة يغيّرها فريق المنصّة، لأنها تمسّ روابط أُرسلت ومبالغ سُجّلت.',
    'identity.saved': 'حُفظت بيانات الحضانة.',
  },

  en: {
    'console.name': 'Nursery admin',
    'console.tagline': 'Daghfal',

    'nav.dashboard': 'Dashboard',
    'nav.children': 'Children',
    'nav.classes': 'Classes',
    'nav.years': 'Academic years',
    'nav.staff': 'Staff',
    'nav.attendance': 'Attendance',
    'nav.daily': 'Daily care',
    'catalog.title': 'Care options',
    'catalog.hint': 'What the quick actions, meals and medication forms choose from. A retired option leaves the lists and stays on the entries that used it.',
    'catalog.type': 'List',
    'catalog.type.MealType': 'Meals',
    'catalog.type.Food': 'Foods',
    'catalog.type.IntakeLevel': 'Drinks',
    'catalog.type.Mood': 'Moods',
    'catalog.type.Activity': 'Activities',
    'catalog.type.DiaperStatus': 'Nappy changes',
    'catalog.type.Occasion': 'Occasions',
    'catalog.type.MedicationForm': 'Medication forms',
    'catalog.empty': 'Nothing in this list yet.',
    'catalog.retired': 'Retired',
    'catalog.retire': 'Retire',
    'catalog.restore': 'Bring back',
    'catalog.editOf': 'Edit {name}',
    'catalog.retireOf': 'Retire {name}',
    'catalog.restoreOf': 'Bring back {name}',
    'catalog.nameAr': 'Name in Arabic',
    'catalog.nameEn': 'Name in English',
    'catalog.icon': 'Icon',
    'catalog.order': 'Order',
    'catalog.unitAr': 'Unit in Arabic',
    'catalog.unitEn': 'Unit in English',
    'catalog.add': 'Add',
    'daily.title': 'Daily care',
    'daily.lede': 'A class’s day, and each child’s, to read, correct and publish.',
    'nav.photos': 'Photos',
    'photos.lede': 'Children’s photos by class, day and occasion: correct who is in a photo and its caption, delete it, or publish again what was withdrawn once a new consent is recorded.',
    'photos.allClasses': 'All classes',
    'photos.statusFilter': 'Photo status',
    'photos.from': 'From',
    'photos.to': 'To',
    'occasions.new': 'New occasion',
    'occasions.hint': 'Photos are grouped under it at upload, and families filter their gallery by it.',
    'occasions.name': 'Occasion name',
    'occasions.date': 'Date',
    'occasions.type': 'Type',
    'occasions.create': 'Add the occasion',
    'occasions.created': 'Occasion added.',
    'daily.recorded': 'Recorded and missing',
    'daily.asleep': '😴 Asleep since {time}',
    'daily.open': 'Open the day',
    'daily.openOf': 'Open the day of {name}',
    'class.empty': 'No children in this class.',
    'nav.finance': 'Finance',
    'nav.complaints': 'Complaints',
    'nav.reports': 'Reports',
    'nav.settings': 'Settings',
    'nav.notifications': 'Notifications',

    'dashboard.title': 'Nursery dashboard',
    'dashboard.registered': 'Registered children',
    'dashboard.presentToday': 'Present today',
    'dashboard.absentToday': 'Absent today',
    'dashboard.incomeThisMonth': 'Income this month',
    'dashboard.collected': 'Collected',
    'dashboard.outstanding': 'Outstanding',
    'dashboard.classes': 'Active classes',
    'dashboard.criticalAllergies': 'Children with a critical allergy',

    'children.title': 'Children',
    'children.lede': 'Every child enrolled at the nursery.',
    'children.search': 'Search by name',
    'childPicker.placeholder': 'Type a name or a guardian’s number',
    'childPicker.matches': 'Matching children',
    'childPicker.none': 'No enrolled child matches what you typed.',
    'childPicker.more': 'More matches not shown: {count}. Type more of the name to narrow them.',
    'childPicker.unchosen': 'Choose the child from the list under the field.',
    'children.new': 'Enrol a child',
    'children.caption': 'Enrolled children',
    'children.empty': 'No children match.',
    'children.column.name': 'Name',
    'children.column.class': 'Class',
    'children.column.dob': 'Date of birth',
    'children.column.age': 'Age',
    'children.column.gender': 'Gender',
    'children.column.status': 'Status',
    'children.allClasses': 'All classes',
    'children.unassigned': 'No class',

    'child.title': 'Child profile',
    'child.enrolled': 'Enrolled',
    'child.enrolled.hint': 'The current enrolment starts on this date. It cannot come after the first day with attendance or care.',
    'child.diaperLog': 'Nappy logging',
    'child.diaperLog.hint': 'For a toddler out of nappies in a younger class, or an older child who still wears one.',
    'child.diaperLog.Class': 'As the class is set',
    'child.diaperLog.On': 'Yes, for this child',
    'child.diaperLog.Off': 'No, for this child',
    'child.diaperLog.yes': 'Nappies are logged',
    'child.diaperLog.no': 'Nappies are not logged',
    'guardians.empty': 'No guardian is linked to this child yet.',
    'guardians.name': 'Guardian’s name',
    'guardians.name.required': 'Enter the guardian’s name.',
    'guardians.edit': 'Edit link',
    'guardians.suspended': 'Suspended',
    'guardians.historyFrom': 'Sees the child’s records from',
    'guardians.historyFrom.hint': 'What a guardian has seen cannot be taken back. Start from today, and widen it later if needed.',
    'guardians.historyFrom.today': 'Today',
    'guardians.historyFrom.enrolment': 'The child’s enrolment date',
    'guardians.historyFrom.date': 'Another date',
    'guardians.historyFrom.shown': 'Sees records from {date}',
    'guardians.suspend': 'Suspend this guardian',
    'guardians.suspend.hint': 'A suspended guardian stays on record, but sees nothing about the child, is sent no notifications, and cannot collect the child.',
    'guardians.suspend.note': 'Reason for the suspension',
    'guardians.suspend.evidence': 'Decision or document number',
    'guardians.suspend.noteRequired': 'Enter the reason for the suspension.',
    'guardians.unlink': 'End the link',
    'guardians.unlink.hint': 'The guardian’s link to the child ends today, and they see nothing about the child after it. The record that they were a guardian stays.',
    'guardians.unlink.confirm': 'Confirm ending the link',
    'guardians.ended.show': 'Former guardians',
    'guardians.ended.hide': 'Hide former guardians',
    'guardians.ended.empty': 'No former guardians.',
    'guardians.ended.Unlinked': 'Unlinked on {date}',
    'guardians.ended.ChildWithdrawn': 'Ended when the child left, on {date}',
    'legalHold.title': 'Legal hold',
    'legalHold.hint': 'While a hold stands, nothing of the child’s records, nor any photo they appear in, is deleted when its retention ends. Only the owner places one, and it is recorded in the audit trail.',
    'legalHold.none': 'No hold. The child’s records are deleted when their retention periods end after the child leaves.',
    'legalHold.held': 'On hold since {date}, placed by {name}',
    'legalHold.held.unknown': 'On hold since {date}',
    'legalHold.reason': 'Reason',
    'legalHold.reason.hint': 'The dispute, complaint or case the hold is for.',
    'legalHold.reason.required': 'Write the reason for the hold.',
    'legalHold.place': 'Place a hold',
    'legalHold.lift': 'Lift the hold',
    'legalHold.lift.confirm': 'Once the hold is lifted, whatever is past its retention is deleted on the next pass. Lift it?',
    'restrictions.title': 'Do not release the child to',
    'restrictions.hint': 'Each name here is shown in red, with its note, to whoever records departures.',
    'restrictions.add': 'Add someone',
    'restrictions.empty': 'Nobody is barred from collecting this child.',
    'restrictions.name': 'Name',
    'restrictions.name.required': 'Enter their name.',
    'restrictions.note': 'What the person at the door must know',
    'restrictions.note.hint': 'Why, and what to do if this person comes.',
    'restrictions.note.required': 'Write a note for the person at the door.',
    'restrictions.evidence': 'Decision or document number',
    'restrictions.evidence.shown': 'Document: {reference}',
    'restrictions.lift': 'Lift',
    'restrictions.lifted': 'Lifted on {date}',
    'restrictions.lifted.show': 'Show lifted ones',
    'restrictions.lifted.hide': 'Hide lifted ones',
    'child.allergies': 'Allergies',
    'child.allergies.noneKnown': 'No known allergies, as the family answered.',
    'child.allergies.unanswered': 'No allergy answer is recorded yet. Add the child\'s allergies, or choose "No known allergies" if the family confirmed it.',
    'child.edit': 'Edit',
    'child.emergencyContacts': 'Emergency contacts',
    'child.emergencyContacts.none': 'No emergency contacts recorded. Add at least one.',
    'child.severity.Mild': 'Mild',
    'child.severity.Severe': 'Severe',
    'child.severity.Anaphylactic': 'Anaphylactic',
    'child.withdraw': 'End enrolment',
    'child.withdraw.reason': 'Reason',
    'child.withdraw.hint':
      'Future invoicing stops and the child’s record and history are kept; nothing is deleted. The guardians’ links end, and they see nothing more about the child except the payments screen while a balance is due.',
    'child.withdraw.copies': 'After the enrolment ends you can make each guardian who asks a copy of the child’s records, from the “Copies of the child’s records” card.',
    'exports.title': 'Copies of the child’s records',
    'exports.hint':
      'A password-protected PDF for one guardian, holding what they see in the app, without photos or video. The password reaches them by text message, and the administration never sees it. Download the file and hand it over.',
    'exports.afterWithdrawal': 'The enrolment has ended. Make a copy here for each guardian who asks, within the period set in Settings.',
    'exports.recipients': 'Who a copy can be made for',
    'exports.make': 'Make a copy',
    'exports.made': 'The copy is ready, and its password was texted to {name}. Download the file below and hand it over.',
    'exports.ended': 'Enrolment ended on {date}',
    'exports.list': 'Copies made',
    'exports.none': 'No copy has been made yet.',
    'exports.covers': 'Covers {from} – {to}',
    'exports.madeBy': 'Made by {name} on {date}',
    'exports.downloads': 'Times downloaded: {count}',
    'exports.keptUntil': 'The file is deleted on {date}',
    'exports.download': 'Download the file',
    'exports.downloading': 'Downloading…',
    'exports.fileGone': 'File deleted',
    'handover.title': 'Photos handed over',
    'handover.hint': 'The copy holds no photos. When you hand the child’s photos to a guardian outside the app, record it here.',
    'handover.guardian': 'Guardian who received them',
    'handover.guardianRequired': 'Choose the guardian who received the photos.',
    'handover.date': 'Handed over on (today if left empty)',
    'handover.note': 'What was handed over, and how',
    'handover.record': 'Record the hand-over',
    'handover.none': 'No hand-over recorded.',
    'handover.entry': '{date}: to {name}, recorded by {by}',
    'child.reenroll': 'Re-enrol',
    'child.reenroll.hint': 'A new enrolment begins; the previous one, and everything recorded in it, stays as it was.',
    'child.reenroll.date': 'Returns on',
    'child.reenroll.restore': 'Guardians who come back with the child',
    'child.reenroll.guardian': '{name} ({relationship})',
    'child.reenroll.noFormer': 'No guardian’s link ended when the child left. Add them from the guardians card after re-enrolling.',
    'child.reenroll.confirm': 'Confirm re-enrolment',
    'child.withdraw.confirm': 'Confirm',
    'child.details': 'Details',
    'import.title': 'Import from a spreadsheet',
    'import.lede': 'Register the nursery’s children at once from a spreadsheet saved as CSV. The file is checked first, and imported only when every row passes.',
    'import.file': 'CSV file',
    'import.file.hint': 'Save the spreadsheet as CSV (UTF-8). The first row holds the column headers.',
    'import.columns': 'Column headers, in Arabic or as follows:',
    'import.unreadable': 'The file could not be read. Save it from the spreadsheet as CSV (UTF-8) and choose it again.',
    'import.empty': 'The file has no rows after the header row.',
    'import.unknownColumns': 'These columns were not read because their headers are not known: {columns}.',
    'import.checking': 'Checking the rows…',
    'import.checked': 'The check',
    'import.done': 'Imported',
    'import.valid': 'Valid: {count}',
    'import.invalid': 'With errors: {count}',
    'import.fixFirst': 'Correct the rows with errors in the spreadsheet, then choose the file again. Nothing is imported until every row passes.',
    'import.commit.one': 'Import one child',
    'import.commit.other': 'Import {count} children',
    'import.committed.one': 'One child is registered.',
    'import.committed.other': '{count} children are registered.',
    'import.rows': 'The file’s rows',
    'import.row': 'Row',
    'import.verdict': 'Result',
    'child.transfer': 'Move to another class',
    'child.transfer.hint': 'The days before the move stay recorded under the class they were in.',
    'child.transfer.to': 'New class',
    'child.transfer.date': 'From',
    'child.transfer.date.hint': 'Today if left empty.',
    'child.transfer.reason': 'Reason',
    'child.transfer.submit': 'Move',
    'child.transfer.done': 'The child is moved.',
    'child.transfer.overCapacity': 'The child is moved, and the class is now over its capacity: {occupancy} of {capacity}.',

    'children.new.title': 'Enrol a child',
    'children.new.firstName': 'First name',
    'children.new.lastName': 'Family name',
    'children.new.preferredName': 'Preferred name',
    'children.new.preferredName.hint': 'What the child is called day to day, if not their first name.',
    'children.new.dob': 'Date of birth',
    'children.new.gender': 'Gender',
    'children.new.class': 'Class',
    'children.new.enrollmentDate': 'Enrolment date',
    'children.new.allergies': 'Allergies',
    'children.new.allergen': 'Allergen',
    'children.new.severity': 'Severity',
    'children.new.reaction': 'Reaction',
    'children.new.actionRequired': 'Action required',
    'children.new.addAllergy': 'Add allergy',
    'children.new.noKnownAllergies': 'No known allergies (the family confirmed it)',
    'children.new.allergies.hint': 'Add the child\'s allergies, or choose "No known allergies". An answer is required.',
    'children.new.emergencyContacts.hint': 'Add at least one emergency contact. Required.',
    'registrationGaps.title': 'Incomplete registrations',
    'registrationGaps.lede': 'Children registered before an emergency contact and an allergy answer became required. Open each child\'s page to complete what is missing.',
    'registrationGaps.column.missing': 'Missing',
    'registrationGaps.contact': 'Emergency contact',
    'registrationGaps.allergies': 'Allergy answer',
    'registrationGaps.empty': 'Every registration is complete.',
    'registrationGaps.notice': 'Children whose registration lacks an emergency contact or an allergy answer: {count}.',
    'registrationGaps.open': 'Show the list',
    'children.new.emergencyContacts': 'Emergency contacts',
    'children.new.contactName': 'Name',
    'children.new.contactRelationship': 'Relationship',
    'children.new.contactPhone': 'Phone',
    'children.new.addContact': 'Add contact',
    'children.new.remove': 'Remove',
    'children.new.submit': 'Enrol',
    'children.new.photo.hint': "Optional. Uploaded once the child is registered; you can add or change it later from the child's page.",
    'children.new.photoFailed': "The child was registered, but the photo could not be uploaded. Add it from the Child's photo card below.",

    'classes.title': 'Classes',
    'classes.lede': 'Classes, their capacity and the age band each serves.',
    'classes.new': 'Add class',
    'classes.caption': 'Classes',
    'classes.empty': 'No classes yet.',
    'classes.column.name': 'Class',
    'classes.column.ages': 'Ages',
    'classes.column.capacity': 'Capacity',
    'classes.column.enrolled': 'Enrolled',
    'classes.column.room': 'Room',
    'classes.column.year': 'Academic year',
    'classes.ageRange': '{min}–{max} months',
    'classes.occupancy': '{enrolled} of {capacity}',
    'classes.full': 'Full',
    'classes.archived': 'Archived',
    'classes.archive': 'Archive',
    'classes.archive.confirm': 'Confirm archiving',
    'classes.archiveOf': 'Archive {name}',
    'classes.editOf': 'Edit {name}',
    'classes.new.name': 'Class name',
    'classes.new.ageMin': 'Youngest age (months)',
    'classes.new.ageMax': 'Oldest age (months)',
    'classes.new.capacity': 'Capacity',
    'classes.new.room': 'Room',
    'classes.new.year': 'Academic year',
    'classes.new.diaperLog': 'Enable nappy logging for this class',
    'classes.new.diaperLog.hint': 'The nappy action appears for teachers only in the younger classes.',
    'classes.new.submit': 'Create class',
    'classes.filter.year': 'Academic year',
    'classes.filter.allYears': 'All years',
    'classes.new.year.current': '{name} (current)',

    'years.title': 'Academic years',
    'years.lede': "Teachers, assistants and reception see a child's records from the first day of the current year. The administration sees every year.",
    'years.new': 'Add year',
    'years.new.submit': 'Add the year',
    'years.edit': 'Edit the year',
    'years.empty': 'No academic years yet.',
    'years.field.name': 'Name, such as 2026/2027',
    'years.field.start': 'Starts',
    'years.field.end': 'Ends',
    'years.status.current': 'Current',
    'years.status.planned': 'Planned',
    'years.status.closed': 'Closed',
    'years.span': 'From {start} to {end}',
    'years.classCount.zero': 'No classes',
    'years.classCount.one': 'One class',
    'years.classCount.other': '{count} classes',

    // ---- The year's fee (Q3, Q44) ----
    'yearFees.title': 'Year fees',
    'finance.tab.payments': 'Payments',
    'yearFees.year': 'Academic year',
    'yearFees.deadline.default': 'This year’s fees are due by {date}. A child can have a different deadline, with a reason.',
    'yearFees.deadline.none': 'This year has no term to take the deadline from. Add its terms on the Academic years page, or enter a deadline for each child.',
    'yearFees.filter.all': 'All children',
    'yearFees.filter.owing': 'Still owing',
    'yearFees.filter.missing': 'No total set',
    'yearFees.filter.paid': 'Paid in full',
    'yearFees.total': 'Total',
    'yearFees.paid': 'Paid',
    'yearFees.remaining': 'Remaining',
    'yearFees.deadline': 'Deadline',
    'yearFees.daysLeft': 'Until the deadline',
    'yearFees.dueToday': 'Today',
    'yearFees.daysLeft.one': '1 day',
    'yearFees.daysLeft.other': '{count} days',
    'yearFees.daysPast.one': '1 day late',
    'yearFees.daysPast.other': '{count} days late',
    'yearFees.notSet': 'Not set',
    'yearFees.set': 'Set total',
    'yearFees.change': 'Change',
    'yearFees.statement': 'Statement',
    'yearFees.statementOf': 'Statement for {name}',
    'yearFees.empty': 'No children in this list.',
    'yearFees.form.title': 'Year fee: {name}',
    'yearFees.form.amount': 'Total for the year',
    'yearFees.form.deadline': 'Deadline',
    'yearFees.form.deadlineHint': 'Leave it as it is for the year’s deadline. A different date needs a reason.',
    'yearFees.form.reason': 'Reason',
    'yearFees.form.reasonHint': 'Needed to change a total already set, and for a deadline other than the year’s. It is kept with the invoice.',
    'years.current.hint': "This is the current year. Moving its start moves the first day of a child's records that teachers see.",
    'years.makeCurrent': 'Make it the current year',
    'years.delete': 'Delete',
    'years.actions': 'Actions',
    'years.terms.caption': 'Terms of {year}',
    'years.terms.empty': 'No terms in this year.',
    'years.term.name': 'Term',
    'years.term.status': 'Status',
    'years.term.open': 'Open',
    'years.term.closed': 'Closed',
    'years.term.new': 'Add term',
    'years.term.new.submit': 'Add the term',
    'years.term.edit': 'Edit the term',
    'years.term.close': 'Close the term',
    'years.term.reopen': 'Reopen the term',
    'years.term.reopen.title': 'Reopen {name}',
    'years.term.reopen.hint': 'Reopening lets this term’s records be changed again: attendance, daily reports, incidents, medication and photos. The reason is kept in the audit trail. Close the term again once the correction is made.',
    'years.term.reopen.reason': 'Why it is reopened',
    'years.term.reopenedNote': 'Reopened on {date}: {reason}',
    'years.rollover': 'End of year',

    'rollover.title': 'End of {year}',
    'rollover.title.plain': 'End of year',
    'rollover.lede': 'Review the next year’s classes and each child’s place in them before closing the year. The proposal is based on each child’s age on the next year’s first day, and all of it can be changed.',
    'rollover.summary': '{year} closes after its last day ({end}), and {next} becomes the current year. Children join their new classes from {start}. No fees are issued for the new year, and anything unpaid stays on the child’s account.',
    'rollover.classes': 'Next year’s classes',
    'rollover.classes.empty': 'This year has no active classes.',
    'rollover.class.carry': 'Carry over',
    'rollover.class.carryOne': 'Carry {name} over to the next year',
    'rollover.class.now': 'Class this year',
    'rollover.class.next': 'Its name in {year}',
    'rollover.class.children': 'Children',
    'rollover.children': 'Children',
    'rollover.children.empty': 'No active children in this year’s classes.',
    'rollover.child.name': 'Child',
    'rollover.child.from': 'Class now',
    'rollover.child.age': 'Age when the next year starts',
    'rollover.child.to': 'Next year',
    'rollover.child.toOne': 'Where {name} goes next year',
    'rollover.child.graduate': 'Graduates',
    'rollover.months.one': '1 month',
    'rollover.months.other': '{count} months',
    'rollover.close': 'Close the year',
    'rollover.close.warning': 'Closing cannot be undone. The year’s open terms ({terms}) are locked and their records become read only, staff assignments to its classes ({assignments}) end on its last day, its classes are archived, and graduates are recorded as withdrawn by graduation, which ends their guardians’ links.',
    'rollover.close.notYet': 'The year can be closed from {date}. Review the plan now and come back on that day.',
    'rollover.close.undecided': 'Choose where {name} goes next year, or graduate them: the class proposed is no longer carried over.',
    'rollover.close.year': 'The year that becomes current',
    'rollover.close.submit': 'Close {year}',
    'rollover.done.title': '{year} is closed, and {next} is now the current year',
    'rollover.done.body': 'New classes: {classes}. Children moved: {moved}. Graduates: {graduated}. Assignments ended: {assignments}. Assign staff to the new classes from the Staff page, and enter next year’s fees from the Year fees page.',
    'rollover.done.back': 'Back to academic years',


    'complaints.title': 'Complaints',
    'complaints.lede': 'Complaints from families, and their handling. A complaint about you is not shown to you here.',
    'complaints.tab.unassigned': 'Unassigned',
    'complaints.tab.mine': 'Mine',
    'complaints.tab.open': 'Open',
    'complaints.tab.all': 'All',
    'complaints.empty': 'Nothing matches.',
    'complaints.subject': 'Subject',
    'complaints.assignedTo': 'With',
    'complaints.unassigned': 'Not yet assigned',
    'complaints.raisedOn': 'Raised',
    'complaints.raisedBy': 'Raised by',
    'complaints.overdue': 'Past the target',
    'complaints.about.is': 'This complaint is about {name}. They do not see it and are not told it exists.',
    'complaints.handling': 'Handling',
    'complaints.acknowledge': 'Confirm it has been read',
    'complaints.start': 'Start work',
    'complaints.assign': 'Assign',
    'complaints.reassign': 'Reassign',
    'complaints.assignTo': 'Hand it to',
    'complaints.assign.hint': 'Only people who may handle complaints appear here, and never the member of staff this one is about.',
    'complaints.assign.choose': 'Choose someone',
    'complaints.openCount': '{count} open',
    'complaints.currentlyWith': 'Currently with {name}',
    'complaints.timeline': 'Handling record',
    'complaints.internal': 'Internal',
    'complaints.note': 'Internal note',
    'complaints.note.who': 'The family never sees this',
    'complaints.note.hint': 'For the record, between colleagues. It does not reach the guardian.',
    'complaints.note.add': 'Save the note',
    'complaints.reply': 'Reply to the guardian',
    'complaints.reply.warning': 'What you write here reaches the guardian and is read exactly as written. For the internal record, use the note box above.',
    'complaints.reply.hint': 'Written to the family.',
    'complaints.reply.send': 'Send to the guardian',
    'complaints.resolve': 'Record the outcome',
    'complaints.resolve.hint': 'This text reaches the guardian and is shown to them as the outcome.',
    'complaints.resolve.summary': 'What was done',
    'complaints.resolve.summary.hint': 'What was done about this complaint? Write it for the guardian.',
    'complaints.close': 'Close the complaint',
    'complaints.reopen': 'Reopen',

    'staff.title': 'Staff',
    'attendance.title': 'Attendance',
    'finance.title': 'Finance',
    'reports.title': 'Reports',
    'reports.month': 'Month',
    'reports.year': 'Year',
    'reports.allClasses': 'All classes',
    'reports.noClasses': 'No classes yet. Create one on the Classes page.',
    'reports.percent': '{value}%',
    'reports.listSeparator': ', ',
    'reports.attendance.title': 'Class attendance',
    'reports.attendance.summary': '{from} to {to}: {operating} open days, {present} days present, {absent} days absent, {notified} days notified.',
    'reports.attendance.days': 'Open days',
    'reports.attendance.present': 'Present',
    'reports.attendance.absent': 'Absent',
    'reports.attendance.notified': 'Notified',
    'reports.attendance.rate': 'Attendance',
    'reports.attendance.empty': 'No children were in this class that month.',
    'reports.dailyCare.title': 'Daily-care completeness',
    'reports.dailyCare.span': '{from} to {to}.',
    'reports.dailyCare.hint': 'Child-days: the days a child was checked in to a class while this person was assigned to it. Complete: those with every routine entry the class board asks for. A class two people share counts for both.',
    'reports.dailyCare.staff': 'Staff member',
    'reports.dailyCare.classes': 'Classes',
    'reports.dailyCare.childDays': 'Child-days',
    'reports.dailyCare.complete': 'Complete',
    'reports.dailyCare.share': 'Share',
    'reports.dailyCare.missing': 'Most often missing',
    'reports.dailyCare.missingItem': '{kind} ({count})',
    'reports.dailyCare.empty': 'No one was assigned to a class that month.',
    'reports.dailyCare.view.staff': 'By class staff',
    'reports.dailyCare.view.recorders': 'By who recorded',
    'reports.dailyCare.recorders.title': 'Daily care by who recorded',
    'reports.dailyCare.recorders.hint': 'Everyone who recorded something that month, and how much of each kind. Child-days: the days they recorded at least one entry for a child. Complete: those with every routine entry the class board asks for, whoever recorded the rest.',
    'reports.dailyCare.recorders.entries': 'Entries',
    'reports.dailyCare.recorders.byKind': 'By kind',
    'reports.dailyCare.recorders.empty': 'No one recorded anything that month.',
    'reports.roster.title': 'Roster',
    'reports.roster.preferred': '{name} ({preferred})',
    'reports.roster.gender': 'Gender',
    'reports.roster.dateOfBirth': 'Date of birth',
    'reports.roster.enrolled': 'Enrolled',
    'reports.roster.guardians': 'Guardians',
    'reports.roster.guardian': '{name} ({relationship})',
    'reports.roster.empty': 'No children enrolled.',
    'finance.printIncome': 'Print income report',
    'yearFees.printBalances': 'Print balances owed',
    'settings.title': 'Nursery settings',
    'identity.title': 'Nursery details',
    'identity.hint': 'The entry link, timezone and currency are changed by the platform team, because they reach links already sent and amounts already recorded.',
    'identity.saved': 'The nursery details are saved.',
  },
};
